/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAtomValue, useSetAtom } from 'jotai';
import callApi, { isCancellationResponse } from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { findingGridDataRefreshAtom, selectedRowIdsAtom } from '../state';

interface PCAssigneeModalProps {
  posture?: any;
  isOpen: boolean;
  toggle: () => void;
  isBulk?: boolean;
}

const PCAssigneeModal: React.FC<PCAssigneeModalProps> = ({
  isOpen,
  posture,
  toggle,
  isBulk = false,
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const refreshFindingsGridData = useSetAtom(findingGridDataRefreshAtom);
  const selectedPostureIds = useAtomValue(selectedRowIdsAtom);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [members, setMembers] = useState([]);
  const [assignee, setAssignee] = useState<any>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);

  // Extract posture details for single posture mode
  const controlId = posture?.control_cid;
  const policyTitle = posture?.policy_title;
  const controlStatement = posture?.control_statement;
  const postureDueDate = posture?.due_date ? new Date(posture.due_date) : null;

  // Fetch members and current assignee
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Build URL for members API
        const url = `/profiles/colleague/new/?search=&is_active=True&nutshell=True&action=assignee&company_id=${companyId}`;

        // Fetch members
        const membersRes = await callApi(url, 'GET', {});

        if (isCancellationResponse(membersRes)) {
          console.log('Request was cancelled');
          return;
        }

        const fetchedMembers =
          membersRes.status === 200 || membersRes.status === 201
            ? membersRes.data.results
            : [];

        setMembers(fetchedMembers);

        // Fetch current assignee if exists
        if (posture?.assignee_id) {
          const currentAssigneeData = fetchedMembers.find(
            (member) => member.id === parseInt(posture.assignee_id),
          );
          setAssignee(currentAssigneeData);

          // Set due date if exists
          if (postureDueDate) {
            setDueDate(postureDueDate);
          }
        }
      } catch (err) {
        showFailureToast(`Failed to load data. ${err}`);
        setError(`Error fetching members: ${err}`);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [companyId, posture, isOpen]);

  /**
   * Handle save action for both assignment and unassignment
   * @param event - Mouse event
   * @param isAssigning - Boolean indicating if this is an assign operation (true) or unassign (false)
   */
  const handleSave = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    isAssigning: boolean = true,
  ): Promise<void> => {
    event.preventDefault();

    setLoading(true);
    setError('');

    try {
      // Prepare data for API call
      const data = {
        // For bulk mode, use selected IDs, otherwise use the single posture ID
        posture_ids: isBulk ? selectedPostureIds : [posture?.id],
        company_id: companyId,
        // If unassigning, don't include assignee_id
        ...(isAssigning && assignee ? { assignee_id: assignee.users_id } : {}),
        // Only include due_date if assigning and due date is set
        ...(isAssigning && dueDate ? { due_date: dueDate.toISOString() } : {}),
      };

      // Make API call
      const endpoint = isAssigning
        ? '/pcm/posture-assignees/bulk_assign/'
        : '/pcm/posture-assignees/bulk_remove/';

      const response = await callApi(endpoint, 'POST', {}, data);

      if (response.status === 200 || response.status === 201) {
        // Show appropriate success message
        const message = isAssigning
          ? `Successfully assigned ${
              isBulk ? selectedPostureIds.length : 1
            } posture(s) to ${assignee?.first_name} ${assignee?.last_name}`
          : `Successfully unassigned ${
              isBulk ? selectedPostureIds.length : 1
            } posture(s)`;

        showSuccessToast(message);

        // Refresh grid data and close modal
        refreshFindingsGridData(Math.random().toString());
        toggle();
      } else {
        throw new Error(
          `Failed to ${isAssigning ? 'assign' : 'unassign'} posture(s)`,
        );
      }
    } catch (err) {
      showFailureToast(`Operation failed. ${err}`);
      setError(`Error: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  // Assign button click handler
  const handleAssign = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    handleSave(event, true);
  };

  // Unassign button click handler
  const handleUnassign = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    handleSave(event, false);
  };

  // Style for the select component
  const menuPortal = (base: any) => ({
    ...base,
    zIndex: 9999,
  });

  // Render the form fields
  const renderFormFields = () => {
    const selectStyles = {
      control: (base: any) => ({
        ...base,
        cursor: 'pointer',
      }),
      option: (base: any) => ({
        ...base,
        cursor: 'pointer',
      }),
      menuPortal: menuPortal,
    };

    const getOptionLabel = (option: any) => {
      return `${option.first_name} ${option.last_name} (${option.email})`;
    };

    const getOptionValue = (option: any) => {
      return option.id;
    };

    return (
      <>
        <Row className="form-group">
          <Col xs="auto" className="d-flex align-items-center">
            <label>
              <strong>Assignee</strong>
            </label>
          </Col>
          <Col>
            <Select
              value={assignee}
              onChange={(selected) => setAssignee(selected)}
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
              placeholder="Select Assignee"
              isClearable
              menuPortalTarget={document.body}
              styles={selectStyles}
              options={members}
            />
          </Col>
        </Row>
        <Row className="form-group">
          <Col xs="auto" className="d-flex align-items-center">
            <label>
              <strong>Due Date (Optional)</strong>
            </label>
          </Col>
          <Col>
            <DatePicker
              selected={dueDate}
              onChange={(date) => {
                setDueDate(date);
              }}
              timeFormat="HH:mm"
              dateFormat="MM-dd-yyyy HH:mm"
              showTimeInput
              isClearable
              className="form-control"
              placeholderText="Select Due Date"
              minDate={new Date()}
              allowSameDay={true}
              wrapperClassName="datePicker"
              popperPlacement="bottom"
              popperProps={{
                strategy: 'fixed',
              }}
            />
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>
        Assign/Unassign Policy Compliance Findings
      </ModalHeader>
      <ModalBody>
        {isBulk ? (
          <Row>
            <Col md="12">
              <>
                <strong>{selectedPostureIds?.length}</strong> findings selected
              </>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md="12">
              <label>
                <p>
                  <strong>Policy: </strong>
                  {policyTitle}
                </p>
              </label>
            </Col>
            <Col md="12">
              <label>
                <p>
                  <strong>Control: </strong>
                  {controlStatement}
                </p>
              </label>
            </Col>
            <Col md="6">
              <label>
                <p>
                  <strong>Control ID: </strong>
                  {controlId}
                </p>
              </label>
            </Col>
          </Row>
        )}
        {isBulk ? (
          <Row>
            <Col>
              <Card>
                <CardBody className="mt-3">
                  <p>
                    Select the assignee and due date for the selected findings.
                    Or click Unassign button to remove the assignments.
                    <br />
                    <br />
                    Note: If the selected findings are already assigned to some
                    user, the association will be updated to this new assignee.
                  </p>
                  {renderFormFields()}
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : (
          renderFormFields()
        )}
        {error && <div className="text-danger">{error}</div>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="secondary"
          onClick={handleUnassign}
          disabled={loading}
          className="mr-2"
        >
          Unassign
        </Button>
        {loading ? (
          <CircleSpinner size={20} color="#0665a9" loading={true} />
        ) : (
          <Button
            color="primary"
            onClick={handleAssign}
            disabled={loading || !assignee}
          >
            Assign
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default PCAssigneeModal;
